/**
 * A colour ramp per category, built in the browser from one hex colour.
 *
 * The tuning client samples matplotlib's ramps at build time and ships the
 * lookup tables. The product needs something different: each category owns a
 * colour, and its view is that colour from pale to deep, so the ramps are
 * generated from `layer.colour` in the manifest. A fifth category arrives with
 * its own ramp for free.
 *
 * The pale end is a tint rather than the page colour. A sequential ramp that
 * starts at the background makes "worst in London" invisible, and this map has
 * three separate ways of meaning "nothing here" already -- under the cutoff,
 * outside a footprint, and no answer at all. The worst place still on the map
 * has to look like a place, which is what `ramp_floor` is for.
 */

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

export function hexToRgb(hex: string): Rgb {
  const clean = hex.replace("#", "");
  const value = parseInt(clean.length === 3
    ? clean.split("").map((c) => c + c).join("")
    : clean, 16);
  return { r: (value >> 16) & 255, g: (value >> 8) & 255, b: value & 255 };
}

export function rgbToCss({ r, g, b }: Rgb, alpha = 1): string {
  return alpha >= 1 ? `rgb(${r} ${g} ${b})` : `rgb(${r} ${g} ${b} / ${alpha})`;
}

function mix(a: Rgb, b: Rgb, t: number): Rgb {
  return {
    r: Math.round(a.r + (b.r - a.r) * t),
    g: Math.round(a.g + (b.g - a.g) * t),
    b: Math.round(a.b + (b.b - a.b) * t),
  };
}

const WHITE: Rgb = { r: 255, g: 255, b: 255 };
/** Not black: a ramp that ends at black reads as a hole rather than as "most". */
const DEEP: Rgb = { r: 17, g: 24, b: 28 };

/** The pale end, and how far the deep end goes toward `DEEP`. */
const TINT = 0.88;
const SHADE = 0.45;
/** Where the category's own colour sits on the ramp. */
const MIDPOINT = 0.55;

export const RAMP_SAMPLES = 256;

/** One category's ramp as `RAMP_SAMPLES` RGBA bytes, which is what a shader samples. */
export function buildRamp(hex: string): Uint8Array {
  const colour = hexToRgb(hex);
  const pale = mix(WHITE, colour, 1 - TINT);
  const deep = mix(colour, DEEP, SHADE);
  const out = new Uint8Array(RAMP_SAMPLES * 4);
  for (let i = 0; i < RAMP_SAMPLES; i++) {
    const t = i / (RAMP_SAMPLES - 1);
    const c = t <= MIDPOINT
      ? mix(pale, colour, t / MIDPOINT)
      : mix(colour, deep, (t - MIDPOINT) / (1 - MIDPOINT));
    out[i * 4] = c.r;
    out[i * 4 + 1] = c.g;
    out[i * 4 + 2] = c.b;
    out[i * 4 + 3] = 255;
  }
  return out;
}

/** The same ramp as CSS stops, for the legend pill. */
export function rampStops(hex: string, stops = 5): string[] {
  const lut = buildRamp(hex);
  return Array.from({ length: stops }, (_, i) => {
    const at = Math.round((i / (stops - 1)) * (RAMP_SAMPLES - 1)) * 4;
    return rgbToCss({ r: lut[at]!, g: lut[at + 1]!, b: lut[at + 2]! });
  });
}
